import * as Phaser from "phaser-ce"
import { log } from "../../../common/Logging"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { BackgroundSprite } from "../../generic-sprites/BackgroundSprite"
import { ButtonContainer, Buttons, ButtonType } from "../../generic-sprites/Buttons"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { Rectangle } from "../../generic-sprites/Shape"
import { SpeechBubble } from "../../generic-sprites/SpeechBubble"
import { Audio } from "../../utils/Audio"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale, x, y } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { DrTimeAudio, DrTimeImage, loadAssets } from "./DrTimeAssets"
import { DrTimeExamRoom } from "./DrTimeExamRoom"
import { DrTimeProgressStore, DrTimeStages } from "./DrTimeProgressStore"
import { PatientType } from "./DrTimeTypes"


enum ActivityStates {
    START = "start",
    LOBBY = "lobby",
    HALLWAY = "hallway"
}

enum HallwayProgress {
    STARTING,
    SAW_VAPER,
    SAW_SMOKER,
    DONE_JUST_SAW_VAPER,
    DONE_JUST_SAW_SMOKER
}

export class DrTimeState extends Phaser.State {

    activityState: ActivityStates

    intro1Background: Phaser.Sprite
    intro2Background: Phaser.Sprite

    intro1BackgroundGroup: Phaser.Group
    intro1TextGroup: Phaser.Group

    intro2BackgroundGroup: Phaser.Group
    intro2TextGroup: Phaser.Group
    intro2IntructionsGroup: Phaser.Group

    afterVaperTextGroup: Phaser.Group
    afterSmokerTextGroup: Phaser.Group

    roomButtonsGroup: Phaser.Group

    room1BackgroundGroup: Phaser.Group
    room2BackgroundGroup: Phaser.Group

    progressButton: ButtonContainer


    // Text
    intro1Text = "Thanks for coming. I’m really busy today and need your help. You’ll be my assistant and examine each patient to see how smoking or vaping even a little bit affects different parts of the body over time. Click “Next” to continue."

    intro2Text = "There are two patients for you to examine. You will use the computer in each of these exam rooms to take the patient’s medical history. As the doctor’s assistant, you’ll have to do five things:"

    intro2Intructions = [
        "1. Ask the patient for their current symptoms",
        "2. Find out about the patient’s tobacco use",
        "3. Review the patient’s chart",
        "4. Select and run a diagnostic test",
        "5. Get a diagnosis from the computer"
    ]

    afterPatient1DoctorAssessmentText = "It looks like you got all the information to make the diagnosis. Unfortunately, it looks like this patient has developed chronic lung disease. She will need life-long treatment with steroids that can cause dangerous side effects. I wish more people understood that vaping even a little bit can build up over time to cause very serious health problems."

    afterPatient2DoctorAssessmentText = "Good work! You’ve gathered everything I need. I will talk with this patient about scheduling his surgery as soon as possible. The blockage in his arteries can cause a heart attack and do serious damage to his heart. Most people don’t realize that smoking even a little bit can do so much harm to your body."

    doctorAllDoneWithPatientsText = "You did a great job helping me figure out what was wrong with my patients. You are now done with this activity. Click “Next” to continue."

    preload() {
        loadAssets(this.game)
        loadGenericAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        if (!PhaserUtils.loggedIn()) {
            PhaserUtils.notLoggedInMessage(this.game)
            return
        }


        // Groups
        this.intro1BackgroundGroup = this.game.add.group()
        this.intro1TextGroup = this.game.add.group()

        this.intro2BackgroundGroup = this.game.add.group()
        this.intro2TextGroup = this.game.add.group()
        this.intro2IntructionsGroup = this.game.add.group()

        this.afterVaperTextGroup = this.game.add.group()
        this.afterSmokerTextGroup = this.game.add.group()

        this.roomButtonsGroup = this.game.add.group()

        this.room1BackgroundGroup = this.game.add.group()
        this.room2BackgroundGroup = this.game.add.group()

        const sawPatient1 = DrTimeProgressStore.instance.patientVaperStageFinished
        const sawPatient2 = DrTimeProgressStore.instance.patientSmokerStageFinished

        /////
        //
        // Set activityState to ActivityStates.LOBBY to skip the Lobby and go straight to the Hallway
        //
        /////
        // If we haven't seen either patient, we're starting with ActivityStates.START. Otherwise, we should skip the lobby.
        if (!sawPatient1 && !sawPatient2) {
            this.activityState = ActivityStates.START
            // this.activityState = ActivityStates.LOBBY
        } else {
            this.activityState = ActivityStates.LOBBY
        }

        this.progressToNextPart()
    }

    // Activity progression
    progressToNextPart = () => {
        switch (this.activityState) {
            case ActivityStates.START:
                this.showLobby()
                this.activityState = ActivityStates.LOBBY
                break
            case ActivityStates.LOBBY:
                this.showHallway()
                this.activityState = ActivityStates.HALLWAY
                break
            case ActivityStates.HALLWAY:
                DrTimeProgressStore.destroy()
                Transitions.startOutro(this.game, ActivityKey.DR_TIME)
                break
            default:
                throw new Error(`Unknown ActivityState: ${this.activityState}`)
        }

    }

    makeProgressionButton = (group: Phaser.Group, enabled: boolean) => {
        this.progressButton = Buttons.create(
            this.game,
            "Next",
            () => {
                this.progressToNextPart()
                this.progressButton.destroy()
            },
            {x: x(0.8), y: y(0.85), width: 0.15, enabled: enabled, group: group, buttonType: ButtonType.PRIMARY}
        )
    }

    goToVaperRoom = () => {
        this.roomButtonsGroup.destroy()
        new DrTimeExamRoom(this.game, PatientType.VAPER)
    }

    goToSmokerRoom = () => {
        this.roomButtonsGroup.destroy()
        new DrTimeExamRoom(this.game, PatientType.SMOKER)
    }

    showLobby = () => {
        // Background
        this.intro1Background = new BackgroundSprite(this.game, DrTimeImage.BG_RECEPTION)
        const doctor = Sprites.add(this.game, DrTimeImage.DR_RECEPTION, {
            x: x(0.534), y: y(0.058),
        })

        this.intro1BackgroundGroup.add(this.intro1Background)
        this.intro1BackgroundGroup.add(doctor)


        // Add text
        SpeechBubble.create(this.game, {text: this.intro1Text, x: Scale.x(0.05), y: Scale.y(0.02), group: this.intro1TextGroup, right: true, width: 0.49})

        // Create Next button
        this.makeProgressionButton(this.intro2BackgroundGroup, false)

        // Turn on Audio
        Audio.add(this.game, DrTimeAudio.LOBBY, {
            onStop: () => {
                this.progressButton.enable()
            }
        })
    }

    showHallway = () => {
        // Destroy old groups
        this.intro1BackgroundGroup.destroy()
        this.intro1TextGroup.destroy()

        // For jumping straight to the patients
        // this.goToVaperRoom()
        // this.goToSmokerRoom()

        this.intro2Background = new BackgroundSprite(this.game, DrTimeImage.BG_HALLWAY)
        const doctor = Sprites.add(this.game, DrTimeImage.DR_HALLWAY, {
            x: Scale.x(0.15), y: Scale.y(0.252)
        })
        this.intro2BackgroundGroup.add(this.intro2Background)
        this.intro2BackgroundGroup.add(doctor)

        // Create Door buttons
        const door1 = new Rectangle(this.game, Scale.x(0.01), Scale.y(0.19), Scale.x(0.18), Scale.y(0.79), {group: this.roomButtonsGroup})
        const door2 = new Rectangle(this.game, Scale.x(0.79), Scale.y(0.21), Scale.x(0.17), Scale.y(0.75), {group: this.roomButtonsGroup})
        door1.graphics.alpha = 0
        door2.graphics.alpha = 0

        const timer = this.game.time.create()

        const sawPatient1 = DrTimeProgressStore.instance.patientVaperStageFinished
        const sawPatient2 = DrTimeProgressStore.instance.patientSmokerStageFinished
        // For skipping the patients
        // const sawPatient1 = true
        // const sawPatient2 = true


        // Turn on Audio
        let audio
        let hallwayProgress: HallwayProgress
        if (!sawPatient1 && !sawPatient2) {
            // Just starting
            audio = DrTimeAudio.HALLWAY_START
            hallwayProgress = HallwayProgress.STARTING
        } else if (sawPatient1 && !sawPatient2) {
            // Saw patient 1 but not 2
            audio = DrTimeAudio.HALLWAY_SAW_PATIENT_VAPER
            hallwayProgress = HallwayProgress.SAW_VAPER
        } else if (sawPatient2 && !sawPatient1) {
            // Saw patient 2 but not 1
            audio = DrTimeAudio.HALLWAY_SAW_PATIENT_SMOKER
            hallwayProgress = HallwayProgress.SAW_SMOKER
        } else {
            // Assume that we just saw the second patient, so play the audio for the last patient completed
            if (DrTimeProgressStore.instance.lastPatientCompleted == DrTimeStages.PATIENT_VAPER) {
                audio = DrTimeAudio.HALLWAY_SAW_PATIENT_VAPER
                hallwayProgress = HallwayProgress.DONE_JUST_SAW_VAPER
            } else {
                audio = DrTimeAudio.HALLWAY_SAW_PATIENT_SMOKER
                hallwayProgress = HallwayProgress.DONE_JUST_SAW_SMOKER
            }
        }
        const hallwayAudio = this.game.add.audio(audio)
        hallwayAudio.onStop.addOnce(() => {
            let clickDoorAudio: string | undefined
            switch (hallwayProgress) {
                case HallwayProgress.STARTING:
                    clickDoorAudio = DrTimeAudio.CLICK_DOOR
                    break
                case HallwayProgress.SAW_VAPER:
                case HallwayProgress.SAW_SMOKER:
                    clickDoorAudio = DrTimeAudio.CLICK_ANOTHER_DOOR
                    break
                case HallwayProgress.DONE_JUST_SAW_VAPER:
                case HallwayProgress.DONE_JUST_SAW_SMOKER:
                    this.intro2TextGroup.destroy()
                    this.intro2TextGroup = this.game.add.group()
                    SpeechBubble.create(this.game, {text: this.doctorAllDoneWithPatientsText, x: x(0.32), y: y(0.1), group: this.intro2TextGroup, width: 0.4})
                    const doneAudio = this.game.add.audio(DrTimeAudio.HALLWAY_DONE)
                    doneAudio.onStop.addOnce(() => {
                        this.makeProgressionButton(this.intro2TextGroup, true)
                    })
                    doneAudio.play()
                    break
                default:
                    throw new Error(`Unknown doctor hallway progress type: ${hallwayProgress}`)
            }

            if (clickDoorAudio != null) {
                const clickDoorAudioPlayer = this.game.add.audio(clickDoorAudio)
                log.debug("Play click door audio.")
                clickDoorAudioPlayer.onStop.addOnce(() => {
                    log.debug("Stop click door audio.")
                    // Make door sprites
                    if (!DrTimeProgressStore.instance.patientVaperStageFinished) {
                        Buttons.makeSpriteButton(door1.invisibleSprite, this.goToVaperRoom)
                    }
                    if (!DrTimeProgressStore.instance.patientSmokerStageFinished) {
                        Buttons.makeSpriteButton(door2.invisibleSprite, this.goToSmokerRoom)
                    }
                })
                clickDoorAudioPlayer.play()
            }

            this.intro2IntructionsGroup.destroy()
        })
        hallwayAudio.onPlay.addOnce(() => {
            timer.start()
        })

        // Add text
        switch (hallwayProgress) {
            case HallwayProgress.STARTING:
                SpeechBubble.create(this.game, {text: this.intro2Text, x: x(0.3), y: y(0.07), group: this.intro2TextGroup, width: 0.53})

                // TODO: Redo timing once final audio gets in
                const stepTimes = [13, 16.5, 20, 23, 27]
                SpeechBubble.createTimed(this.game, {
                    texts: this.intro2Intructions,
                    stepTimes,
                    timer,
                    x: x(0.31),
                    y: y(0.09),
                    group: this.intro2IntructionsGroup,
                    width: 0.48,
                })
                timer.add(12 * 1000, () => {
                        this.intro2TextGroup.destroy()
                    }
                )
                break
            case HallwayProgress.SAW_VAPER:
            case HallwayProgress.DONE_JUST_SAW_VAPER:
                SpeechBubble.create(this.game,
                    {text: this.afterPatient1DoctorAssessmentText, x: x(0.31), y: y(0.02), group: this.afterVaperTextGroup, width: 0.65})
                hallwayAudio.onStop.addOnce(() => this.afterVaperTextGroup.destroy())
                break
            case HallwayProgress.SAW_SMOKER:
            case HallwayProgress.DONE_JUST_SAW_SMOKER:
                SpeechBubble.create(this.game,
                    {text: this.afterPatient2DoctorAssessmentText, x: x(0.3), y: y(0.04), group: this.afterSmokerTextGroup, width: 0.65})
                hallwayAudio.onStop.addOnce(() => this.afterSmokerTextGroup.destroy())
                break
            default:
                log.error(`Unknown doctor hallway progress type: ${hallwayProgress}`)
        }


        hallwayAudio.play()
    }

}
